/**
 * Hook encapsulating the criteria chatbot state machine for CreateRole.
 */

import { useState, useCallback, useEffect } from 'react';
import { criteriaChatAPI, ChatMessage } from '../api/client';
import { AxiosError } from 'axios';

interface UseCriteriaChatbotOptions {
  roleTitle: string;
  draftRoleId: string | null;
  useChatbot: boolean;
}

export function useCriteriaChatbot({ roleTitle, draftRoleId, useChatbot }: UseCriteriaChatbotOptions) {
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>([]);
  const [userMessage, setUserMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [isCriteriaComplete, setIsCriteriaComplete] = useState(false);
  const [isFinalizationInProgress, setIsFinalizationInProgress] = useState(false);
  const [lastSuggestions, setLastSuggestions] = useState<string[]>([]);
  const [criteriaText, setCriteriaText] = useState('');
  const [parsedDimensions, setParsedDimensions] = useState<any[] | null>(null);
  const [parsedFlags, setParsedFlags] = useState<any | null>(null);
  const [parsedThresholds, setParsedThresholds] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startChatbot = useCallback(async () => {
    setChatLoading(true);
    try {
      const response = await criteriaChatAPI.start(roleTitle || undefined);
      setConversationHistory([
        { role: 'assistant', content: response.data.message }
      ]);
    } catch (err) {
      console.error('Failed to start chatbot:', err);
      setError('Failed to start criteria chatbot. Please try manual entry.');
    } finally {
      setChatLoading(false);
    }
  }, [roleTitle]);

  useEffect(() => {
    if (useChatbot && conversationHistory.length === 0) {
      startChatbot();
    }
  }, [useChatbot, conversationHistory.length, startChatbot]);

  const sendChatMessage = useCallback(async () => {
    if (!userMessage.trim()) return;

    const newUserMessage: ChatMessage = { role: 'user', content: userMessage };
    const updatedHistory = [...conversationHistory, newUserMessage];
    setConversationHistory(updatedHistory);

    const finalizationKeywords = [
      'finalize', 'done', 'ready', 'looks good', 'that\'s good',
      'perfect', 'complete', 'finish', 'go ahead', 'approved'
    ];
    const messageToSend = userMessage.toLowerCase();
    const isFinalization = finalizationKeywords.some(k => messageToSend.includes(k));

    setUserMessage('');
    setChatLoading(true);
    setIsFinalizationInProgress(isFinalization);

    if (isFinalization) {
      localStorage.setItem('critical_operation_in_progress', 'chat_finalization');
    }

    try {
      const response = await criteriaChatAPI.chat({
        conversation_history: conversationHistory,
        user_message: userMessage,
        role_title: roleTitle || undefined,
        role_id: draftRoleId || undefined
      });

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.data.message
      };
      const finalHistory = [...updatedHistory, assistantMessage];
      setConversationHistory(finalHistory);

      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setLastSuggestions(response.data.suggestions);
      }

      if (response.data.is_complete && response.data.parsed_criteria) {
        setIsCriteriaComplete(true);
        setCriteriaText(response.data.parsed_criteria.criteria_text);
        setParsedDimensions(response.data.parsed_criteria.dimensions);
        setParsedFlags(response.data.parsed_criteria.flags);
        setParsedThresholds(response.data.parsed_criteria.thresholds);
      }
    } catch (err) {
      console.error('Chat error:', err);

      if (err instanceof AxiosError && err.response?.status === 401) {
        setError('Your session has expired. Please refresh the page to log in again and continue where you left off.');
        localStorage.removeItem('critical_operation_in_progress');
        return;
      }

      const isTimeout = err && typeof err === 'object' && 'code' in err && err.code === 'ECONNABORTED';
      if (isTimeout) {
        setError('Request timed out. You can try sending your message again or refresh the page to continue.');
      } else {
        setError('Failed to send message. You can try again.');
      }
    } finally {
      setChatLoading(false);
      setIsFinalizationInProgress(false);
      localStorage.removeItem('critical_operation_in_progress');
    }
  }, [userMessage, conversationHistory, roleTitle, draftRoleId]);

  const resetChatbot = useCallback(() => {
    setConversationHistory([]);
    setCriteriaText('');
    setIsCriteriaComplete(false);
    setLastSuggestions([]);
    localStorage.removeItem('criteria_chat_history');
    localStorage.removeItem('criteria_text');
    localStorage.removeItem('criteria_complete');
    startChatbot();
  }, [startChatbot]);

  return {
    conversationHistory,
    setConversationHistory,
    userMessage,
    setUserMessage,
    chatLoading,
    isCriteriaComplete,
    setIsCriteriaComplete,
    isFinalizationInProgress,
    lastSuggestions,
    criteriaText,
    setCriteriaText,
    parsedDimensions,
    parsedFlags,
    parsedThresholds,
    error,
    setError,
    sendChatMessage,
    startChatbot,
    resetChatbot,
  };
}
